import React, { useState } from 'react'
import { X, UserPlus, Mail, Shield, CreditCard, CheckCircle, AlertCircle, UserX } from 'lucide-react'
import { agencyService } from '../services/agencyService'

interface AddAgentModalProps {
  isOpen: boolean
  agencyId: string
  agencyName: string
  onClose: () => void
  onAgentAdded: () => void
}

interface BulkResult {
  added: string[]
  alreadyExist: string[]
  notFound: string[]
}

export const AddAgentModal: React.FC<AddAgentModalProps> = ({
  isOpen,
  agencyId,
  agencyName,
  onClose,
  onAgentAdded
}) => {
  const [emailsText, setEmailsText] = useState('')
  const [role, setRole] = useState<'agent' | 'admin'>('agent')
  const [paymentResponsibility, setPaymentResponsibility] = useState<'agent' | 'agency'>('agency')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<BulkResult | null>(null)

  const parseEmails = (text: string): string[] => {
    return text
      .split('\n')
      .map(e => e.trim().toLowerCase())
      .filter(e => e.length > 0 && e.includes('@'))
  }

  const emailCount = parseEmails(emailsText).length

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const emails = parseEmails(emailsText)

    if (emails.length === 0) {
      setError('Please enter at least one valid email address')
      return
    }

    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const data = await agencyService.bulkAddAgents(agencyId, { emails, role, paymentResponsibility })
      setResult(data)
      if (data.added.length > 0) {
        onAgentAdded()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add agents')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setEmailsText('')
    setRole('agent')
    setPaymentResponsibility('agency')
    setError(null)
    setResult(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg max-w-lg w-full mx-4 max-h-screen overflow-y-auto border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <UserPlus className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Bulk Add Agents</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Add multiple agents directly to {agencyName}</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Info banner */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 rounded-lg text-sm">
            Agents will be added immediately with an active license, bypassing the invitation process. Users must already have an account.
          </div>

          {/* Results panel */}
          {result && (
            <div className="space-y-3">
              {result.added.length > 0 && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300 font-medium text-sm mb-1">
                    <CheckCircle className="w-4 h-4" />
                    {result.added.length} agent{result.added.length !== 1 ? 's' : ''} added
                  </div>
                  <ul className="text-xs text-green-600 dark:text-green-400 space-y-0.5 ml-6">
                    {result.added.map(e => <li key={e}>{e}</li>)}
                  </ul>
                </div>
              )}
              {result.alreadyExist.length > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 font-medium text-sm mb-1">
                    <AlertCircle className="w-4 h-4" />
                    {result.alreadyExist.length} already in agency
                  </div>
                  <ul className="text-xs text-amber-600 dark:text-amber-400 space-y-0.5 ml-6">
                    {result.alreadyExist.map(e => <li key={e}>{e}</li>)}
                  </ul>
                </div>
              )}
              {result.notFound.length > 0 && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-medium text-sm mb-1">
                    <UserX className="w-4 h-4" />
                    {result.notFound.length} not found / no account
                  </div>
                  <ul className="text-xs text-red-600 dark:text-red-400 space-y-0.5 ml-6">
                    {result.notFound.map(e => <li key={e}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!result && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Emails textarea */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Addresses
                  {emailCount > 0 && (
                    <span className="ml-2 text-xs text-slate-500 dark:text-slate-400 font-normal">({emailCount} email{emailCount !== 1 ? 's' : ''})</span>
                  )}
                </label>
                <textarea
                  value={emailsText}
                  onChange={(e) => setEmailsText(e.target.value)}
                  rows={6}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none font-mono text-sm"
                  placeholder={"agent1@example.com\nagent2@example.com\nagent3@example.com"}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">One email address per line</p>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <Shield className="w-4 h-4 inline mr-2" />
                  Role
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'agent' | 'admin')}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="agent">Agent</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Payment Responsibility */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <CreditCard className="w-4 h-4 inline mr-2" />
                  Payment Responsibility
                </label>
                <select
                  value={paymentResponsibility}
                  onChange={(e) => setPaymentResponsibility(e.target.value as 'agent' | 'agency')}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="agency">Agency Pays</option>
                  <option value="agent">Agent Pays</option>
                </select>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || emailCount === 0}
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      Add {emailCount > 0 ? `${emailCount} Agent${emailCount !== 1 ? 's' : ''}` : 'Agents'}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Close button shown after results */}
          {result && (
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { setResult(null); setEmailsText('') }}
                className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              >
                Add More
              </button>
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AddAgentModal
